import { TaskViewModel } from '../ViewModels';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useAuditEnricher,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';

import { TaskHeader } from '../task-config';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useTask } from '@/OneLiners';
import { TaskModel } from '../TaskModel';

/**
 * useTaskTableConfig hook
 *
 * Domain-specific layout composition hook for Task entities.
 * Orchestrates domain data, audit enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Task domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Task table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<TaskViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<TaskViewModel>
 */
export function useTaskTableConfig(props: DomainHookProps): DomainTableConfig<TaskViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Task;

    const { TaskViewEnums } = useAppEnum();

    const taskHook = useTask();

    /**
     * Enrichment layer (audit-based)
     * Responsible for resolving enriched Task data
     */
    const { enricherResponse, getEnrichedAudit, } = useAuditEnricher<TaskModel>();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            taskHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: taskHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: taskHook,

        /**
         * Header configuration for Property table.
         */
        headers: TaskHeader.build(TaskViewEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (task: TaskModel, isDisplaying: boolean) =>
            new TaskViewModel({
                task: getEnrichedAudit(task),
                viewEnums: TaskViewEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}